/**
 * Page check: the page confirms it is current instead of waiting to be told.
 *
 * Every signed-in page carries its own version on <body data-page-version>
 * (the page ETag, application/utils/page_etag.py), however it was painted:
 * from the service worker's saved copy, from the network, or from a Turbo
 * back/forward snapshot (a restore visit makes no request at all). On every
 * turbo:load, and whenever the app comes back to the foreground
 * (chippie:app-lifecycle state=active, which browser, PWA and the native shells
 * all raise), the page asks with one ordinary request to its own URL carrying
 * that version as If-None-Match:
 *
 *   304            the page is current;
 *   200            it changed: the worker has stored the current copy
 *                  (static/js/sw/navigation-cache.js pageCheckHandler), and
 *                  the page swaps to it with a replace visit, but never while
 *                  the user is busy (an open dialog, menu or file picker, a field being
 *                  typed in, or edits not yet saved): the swap would replace the
 *                  body and destroy it. It waits and swaps once they finish;
 *   404/410/login  the saved copy is gone: the swap shows the real answer;
 *   no answer      unconfirmed, never assumed current: asked again on the
 *                  next trigger. A failed request also marks the page as a
 *                  saved offline copy, which must not submit, except a form
 *                  the offline-intent controller queues through PowerSync.
 *
 * Request and response, not a message from the worker: the old one-way
 * PAGE_REVALIDATED post could be dropped (WebKit loses worker messages around
 * navigations), and the page then accepted saves against stale data. Without
 * a worker the same request goes straight to the server and answers the same.
 *
 * Saves are not held while a check is in flight. A save that overwrites
 * fields is checked by the server against the record version and refused as
 * a conflict the user resolves (record_conflict_controller.js); additive
 * saves never conflict (the checked set is CHECKED_SAVES in
 * application/web/record_version_header.py).
 *
 * A save's Turbo Stream patches the page without moving data-page-version, so
 * the next check answers 200 and the page swaps once: one extra repaint,
 * accepted, because the full-page hash is unknown without rendering the page.
 *
 * Code that must re-render the page after a change calls refresh(), never
 * Turbo.visit(location.href). Design and the write contract:
 * docs/technical/features/PWA_CACHE_STRATEGY.md.
 */
(function () {
  'use strict';

  if (window.chippiePageCheck) return;

  var OFFLINE_MESSAGE = 'This is a saved offline page. Reconnect before applying changes.';

  var checking = null;      // URL with a check in flight
  var recheck = false;      // a refresh asked for while that check was in flight
  var swapping = false;     // the next turbo:load is our own swap: already current
  var swapUrl = null;       // the URL that swap re-visits
  var swapStarted = false;  // Turbo started that visit (it was not cancelled)
  var offlineUrl = null;    // URL whose last check could not reach the server
  var waitTimer = null;     // a changed page's swap, waiting for the user to finish
  var pickingFile = false;  // a native file picker is open (see userIsBusy)

  var WAIT_RETRY_MS = 2000;
  // Open dialogs and menus, closed by the swap. #mobileMenuOverlay is the phone's
  // More menu (mobile_controller.js); .dropdown-menu covers navbar menus and the bell;
  // dialog[open] covers native dialogs such as the photo viewer.
  var OPEN_UI = '.modal.show, .offcanvas.show, .dropdown-menu.show, #mobileMenuOverlay.show, dialog[open]';
  var EDITABLE = { INPUT: true, TEXTAREA: true, SELECT: true };
  var TEXT_TYPES = { text: true, textarea: true, search: true, email: true, tel: true,
    url: true, number: true };

  // What the checks of this page view found, one letter each, handed to
  // rum-timing.js once the user navigates away: a page left stale on a phone is
  // otherwise invisible from the server.
  //   R asked   C current (304)   V changed (200)   G gone (404/410/redirect)
  //   S swap issued   L swap rendered (an S with no L never reached the screen)
  //   W swap waiting for the user to finish
  //   O no answer (offline)   E server error, unconfirmed
  var TRACE_MAX = 40;
  var viewTrace = '';
  var finishedTrace = null;

  function trace(code) {
    if (viewTrace.length < TRACE_MAX) viewTrace += code;
  }

  function pageVersion() {
    return (document.body && document.body.dataset.pageVersion) || '';
  }

  /**
   * Is the user in the middle of something the swap would destroy? A swap
   * replaces the whole body, so an open dialog or menu, the field being typed
   * in, or a field holding text the page did not render with (typed, then the
   * user went to another app, so nothing has focus on return) would be lost. An
   * open inline editor counts on its own: the description editor is
   * contenteditable over a hidden textarea, which the field scan below cannot
   * see. Edits a form registered with _unsaved_work.js count too: a changed
   * select or checkbox is invisible to the field scan, and the swap's visit
   * would raise that guard's leave prompt with no navigation by the user.
   * An open file picker counts too: it looks idle (its menu has closed, nothing
   * has focus), but the file picked in it arrives as a change event on the
   * input that opened it, and a swap meanwhile detaches that input, dropping
   * the photo with no upload and no error.
   */
  function userIsBusy() {
    if (pickingFile) return true;
    if (document.querySelector(OPEN_UI)) return true;
    if (window.chippieUnsavedWork && window.chippieUnsavedWork.any()) return true;
    var editors = document.querySelectorAll('.inline-edit-form:not(.d-none)');
    for (var e = 0; e < editors.length; e++) {
      if (editors[e].offsetParent !== null) return true;
    }
    var active = document.activeElement;
    if (active && (active.isContentEditable || EDITABLE[active.tagName])) return true;
    var fields = document.querySelectorAll('input, textarea');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.offsetParent === null || !TEXT_TYPES[field.type]) continue;  // hidden or not text
      if (field.value !== field.defaultValue) return true;
    }
    return false;
  }

  /**
   * Swap once the user is not busy. The worker already holds the current copy,
   * so waiting costs no request: the page just retries until the editor or
   * dialog is gone. A save made meanwhile is still protected by the server's
   * record version check.
   */
  function swapWhenFree(url) {
    clearTimeout(waitTimer);
    waitTimer = null;
    if (location.href !== url) return;
    if (!userIsBusy()) { swap(); return; }
    if (viewTrace.slice(-1) !== 'W') trace('W');
    waitTimer = setTimeout(function () { swapWhenFree(url); }, WAIT_RETRY_MS);
  }

  /**
   * Turbo announces a visit it starts with turbo:visit before Turbo.visit
   * returns. No announcement means a turbo:before-visit listener cancelled it,
   * possibly one that also stopped the event before page check's own listener
   * saw it, so the swap is over and the next turbo:load is a real page.
   */
  function swap() {
    if (!(window.Turbo && typeof window.Turbo.visit === 'function')) return;
    trace('S');
    swapping = true;
    swapStarted = false;
    swapUrl = location.href;
    window.Turbo.visit(location.href, { action: 'replace' });
    if (!swapStarted) swapping = false;
  }

  function settle(url, version, response) {
    if (location.href !== url || pageVersion() !== version) return;  // moved on
    offlineUrl = null;
    if (response.status === 304) { trace('C'); return; }
    if (response.status === 200 && !response.redirected) { trace('V'); swapWhenFree(url); return; }
    if (response.redirected || response.status === 404 || response.status === 410) {
      trace('G');
      swapWhenFree(url);
      return;
    }
    trace('E');
  }

  /** Ask the server whether the page in view is current. False: nothing to ask with. */
  function check() {
    var version = pageVersion();
    var url = location.href;
    if (!version) return false;
    if (checking === url) return true;
    checking = url;
    trace('R');
    fetch(url, {
      headers: {
        Accept: 'text/html',
        'If-None-Match': '"' + version + '"',
        'X-Chippie-Page-Check': '1'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      settle(url, version, response);
    }).catch(function () {
      if (location.href === url) offlineUrl = url;
      trace('O');
    }).then(function () {
      if (checking === url) checking = null;
      if (recheck) {
        recheck = false;
        check();
      }
    });
    return true;
  }

  /**
   * The data under this page changed (the user's own save that answered JSON,
   * or a realtime signal): show the server's current render. Asks the check's
   * question rather than re-visiting the URL, because a Turbo visit is answered
   * cache-first with the copy saved BEFORE the change, and the user could act
   * on its rows until a check swapped them out. A check already in flight may
   * have been rendered before the change committed, so the page asks again
   * once it answers. A page with no version has no check, so it reloads from
   * the network (hard navigations are network-first).
   */
  function refresh() {
    if (checking === location.href) {
      recheck = true;
      return;
    }
    if (!check()) location.reload();
  }

  /**
   * A save here changed another page too (a time tap on the job page changes
   * the dashboard's clock card): bring the device's saved copy of that page up
   * to date while there is a connection, so a later visit with no signal does
   * not paint the copy saved before the save. The same request as a check with
   * no version to confirm, so the worker's pageCheckHandler stores the 200;
   * marked as a prefetch, so the render never takes the user's flash messages
   * (application/utils/page_prefetch.py). Nothing is swapped and nothing waits
   * on it; a failure leaves the copy for the page's own check to correct.
   */
  function refreshCopy(url) {
    fetch(url, {
      headers: { Accept: 'text/html', 'X-Chippie-Page-Check': '1', 'X-Sec-Purpose': 'prefetch' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).catch(function () {});
  }

  document.addEventListener('turbo:before-visit', function (event) {
    clearTimeout(waitTimer);   // the user is leaving: a waiting swap is moot
    waitTimer = null;
    if (swapping) {
      // A visit elsewhere makes Turbo abandon the swap, so the next
      // turbo:load is a real page and must be checked. A cancelled swap is
      // caught in swap() itself.
      if (event.detail && event.detail.url === swapUrl) return;   // the swap itself
      if (event.defaultPrevented) return;   // a visit elsewhere that never started
      swapping = false;
    }
    if (viewTrace) finishedTrace = location.pathname + ':' + viewTrace;
    viewTrace = '';
  });

  document.addEventListener('turbo:visit', function (event) {
    if (swapping && event.detail && event.detail.url === swapUrl) swapStarted = true;
  });

  document.addEventListener('turbo:load', function () {
    offlineUrl = null;
    // The page our own swap just painted came from the network a moment ago.
    // Checking it again would swap forever on a page whose HTML varies per render.
    if (swapping) { swapping = false; trace('L'); return; }
    check();
  });

  document.addEventListener('chippie:app-lifecycle', function (event) {
    if (event.detail && event.detail.state === 'active') check();
  });

  // The file picker's open span, for userIsBusy. Capture phase, so a label that
  // opens the input (the Add Photo menu) is seen too: its click is re-dispatched
  // on the input. A browser without the cancel event leaves the flag set after
  // a cancelled pick; that only holds a changed page's swap until the next visit.
  function isFileInput(target) {
    return !!target && target.tagName === 'INPUT' && target.type === 'file';
  }
  document.addEventListener('click', function (event) {
    if (isFileInput(event.target)) pickingFile = true;
  }, true);
  function filePickerClosed(event) {
    if (isFileInput(event.target)) pickingFile = false;
  }
  document.addEventListener('change', filePickerClosed, true);
  document.addEventListener('cancel', filePickerClosed, true);
  document.addEventListener('turbo:before-visit', function () { pickingFile = false; });

  /**
   * A form the offline-intent controller will queue through PowerSync is how a
   * save is made with no connection (offline_intent_controller.js), so the
   * guard below lets it through. The guard listens in the capture phase and
   * would otherwise stop it before the controller's own submit listener runs.
   * Without a local database, or on a form marked online-only, the controller
   * steps aside and the guard still holds.
   */
  function queuesOffline(form) {
    return !!(window.chippiePowerSync &&
      form.closest('[data-controller~="offline-intent"]') &&
      !form.hasAttribute('data-offline-intent-online-only'));
  }

  // A page shown from the saved copy while the server cannot be reached must
  // not submit: the save would fail or be lost. Everything else goes through.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    var method = form && form.getAttribute ? form.getAttribute('method') : '';
    if (String(method || 'get').toLowerCase() === 'get') return;
    if (offlineUrl !== location.href) return;
    if (queuesOffline(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (typeof window.showNotification === 'function') {
      window.showNotification(OFFLINE_MESSAGE, 'warning');
    }
  }, true);

  window.chippiePageCheck = {
    check: check,
    refresh: refresh,
    refreshCopy: refreshCopy,
    /** True while the page's own swap visit is under way (turbo-compat.js lets it through). */
    isSwapping: function () { return swapping; },
    /** The trace of the page view the user just left, once; null if none. */
    takeLastViewTrace: function () {
      var finished = finishedTrace;
      finishedTrace = null;
      return finished;
    }
  };

  /**
   * Called by both native shells (reloadAfterSheet in MainActivity.kt and
   * MainViewController.swift) when a native sheet closes. The sheet saved in its
   * own WebView, so this page may be the pre-save render. What the page does
   * about it is web policy and lives here, where a deploy can change it; the
   * string a shell injects ships only with a store release.
   */
  window.chippieAfterSheet = refresh;
})();
